import { canTransition } from './transition.js';
/**
 * Prescription lifecycle. Appendix C.4 and W.4.
 *
 * `draft → active → superseded` · `active → expired` (date-driven) · `draft → discarded`.
 *
 * `RX-002` is **unqualified**: "Prescriptions are versioned and immutable." Not "active ones", not
 * "until they expire". Appendix C.4 happens to phrase immutability around `active` and `superseded`,
 * and reading that literally would let a prescription become freely editable and deletable the day
 * its expiry date passes, while jobs hold snapshots taken against it. **Any immutability trigger
 * must key on the row having ever left `draft`, never on its current status name.**
 *
 * A correction is a new version with a
 * supersede link, never an edit, because a job holds a frozen snapshot and a rewritten Rx would
 * silently disagree with what was manufactured.
 */
export const PRESCRIPTION_STATUSES = [
    'draft',
    'active',
    'superseded',
    'expired',
    'discarded',
];
export const PRESCRIPTION_TRANSITIONS = {
    draft: ['active', 'discarded'],
    active: ['superseded', 'expired'],
    // Terminal. Appendix C.4 gives no edge out of either, and that is the immutability guarantee:
    // a superseded version is the historical record a job's snapshot was taken against.
    superseded: [],
    expired: [],
    discarded: [],
};
export function planActivation(status) {
    if (status === 'active')
        return { ok: true, action: 'already_active' };
    if (canTransition(PRESCRIPTION_TRANSITIONS, status, 'active')) {
        return { ok: true, action: 'activate' };
    }
    return {
        ok: false,
        code: 'RX_NOT_DRAFT',
        message: `This prescription is ${status} and cannot be issued. Only a draft can be.`,
    };
}
/**
 * Whether a prescription can be corrected, and if so what its successor is. `RX-002`; Appendix C.4.
 *
 * Pure: the caller supplies the predecessor's status and version and the reason. The predecessor
 * must be `active`, the only status with an edge to `superseded`. `expired` has no outgoing edge,
 * so an expired prescription is refused with a sentence that says so (`decisions/0024`). The reason
 * is required and non-empty after trimming, which the `prescription_supersede_has_reason` check
 * also enforces in the database.
 */
export function planSupersede(input) {
    if (!Number.isInteger(input.version) || input.version < 1) {
        return {
            ok: false,
            code: 'RX_VERSION_INVALID',
            message: `Version ${String(input.version)} is not a prescription version.`,
        };
    }
    if (input.status === 'expired') {
        return {
            ok: false,
            code: 'RX_EXPIRED',
            message: 'This prescription has expired and an expired prescription cannot be corrected. Record a new prescription instead.',
        };
    }
    if (!canTransition(PRESCRIPTION_TRANSITIONS, input.status, 'superseded')) {
        return {
            ok: false,
            code: 'RX_NOT_ACTIVE',
            message: input.status === 'draft'
                ? 'This prescription has not been issued yet. Issue it first, or record a new one.'
                : `This prescription is ${input.status} and cannot be corrected. Only an active prescription can be.`,
        };
    }
    if (typeof input.reason !== 'string' || input.reason.trim() === '') {
        return {
            ok: false,
            code: 'RX_REASON_REQUIRED',
            message: 'A correction needs a reason.',
        };
    }
    return { ok: true, successorVersion: input.version + 1 };
}
//# sourceMappingURL=prescription.js.map